import { ChangeDetectorRef, Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ErrorService } from '@app/services/error/error.service';
import { PageTitleService } from '@app/services/page-title/page-title.service';
import { NavController, RefresherCustomEvent } from '@ionic/angular';
import { Logger, LoggingService } from 'ionic-logging-service';
import { OrderPipe } from 'ngx-order-pipe';
import { of } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { LoginService } from 'src/app/services/login/login.service';
import { NetworkService } from 'src/app/services/network/network.service';
import { PlatformService } from 'src/app/services/platform/platform.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { ITenantLite } from 'src/app/services/tenant/models/tenant-lite.model';
import { TenantService } from 'src/app/services/tenant/tenant.service';
import { PostAuthNavigationService } from '@app/services/post-auth-navigation/post-auth-navigation.service';

@Component({
    selector: 'app-tenant-select',
    templateUrl: './tenant-select.page.html',
    styleUrls: ['./tenant-select.page.scss'],
    standalone: false
})
export class TenantSelectPage implements OnInit {
  public isOnline = this.networkService.hasInternetAccess;
  private logger: Logger;
  private registrationCode: string = null;
  public isCordova: boolean = this.platformService.isCordova();
  public  showDotsSignal = signal<boolean>(true);
  public  tenantListSignal = signal<ITenantLite[]>([]);
  private currentTenantList: ITenantLite[] = [];

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private errorService: ErrorService,
    private loginService: LoginService,
    private loggingService: LoggingService,
    private navController: NavController,
    private networkService: NetworkService,
    public orderPipe: OrderPipe,
    private pageTitleService: PageTitleService,
    private platformService: PlatformService,
    private postAuthNavigationService: PostAuthNavigationService,
    private route: ActivatedRoute,
    private storageService: StorageService,
    private tenantService: TenantService
  ) {
    this.logger = this.loggingService.getLogger("[TenantSelectPage]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  ngOnInit() {
    this.pageTitleService.setTitle('ChangeHome');
    if (this.isCordova) {
      this.storageService.getDatabaseState().pipe(
        filter(isReady => isReady),
        take(1)
      ).subscribe(() => this.initTenantSelection());
    } else {
      this.initTenantSelection();
    }
  }

  // #################################### //
  // ### INIT TENANT SELECTION  ######### //
  // #################################### //

  /**
   * Initializes the tenant selection list.
   *
   * Reads from TenantService.tenantListSignal if already populated (e.g. post-auth flow
   * where LoginService.fetchTenantList$ has already fetched the list). Otherwise, fetches
   * the tenant list from the server. This covers both the post-auth and "Change Home" flows
   * with a single code path.
   */
  public initTenantSelection(): void {
    this.route.queryParams.pipe(
      take(1),
    ).subscribe(params => {
      this.registrationCode = params['registrationCode'] || null;

      const cachedTenantList = this.tenantService.tenantListSignal();
      if (cachedTenantList && cachedTenantList.length > 0) {
        this.logger.info('initTenantSelection() - Using cached tenant list from signal, length: ' + cachedTenantList.length);
        this.showTenantList(cachedTenantList);
      } else {
        this.logger.info('initTenantSelection() - No cached tenant list, fetching from server');
        this.fetchAndShowTenantList();
      }
    });
  }

  // #################################### //
  // ### FETCH AND SHOW TENANT LIST ##### //
  // #################################### //

  /**
   * Fetches the tenant list from the server and displays it in the UI.
   * Used when the cached signal is empty and for pull-to-refresh.
   */
  private fetchAndShowTenantList(event?: RefresherCustomEvent): void {
    const refresher = event?.target as HTMLIonRefresherElement | undefined;
    this.loginService.fetchTenantList$().pipe(
      take(1),
      tap((tenantList: ITenantLite[]) => {
        this.logger.info('fetchAndShowTenantList() - got TenantList with length: ' + tenantList.length);
        if (refresher) {
          refresher.complete();
        }
      }),
      switchMap((tenantList: ITenantLite[]) => {
        if (!tenantList || tenantList.length === 0) {
          // Empty list = no tenant access. Hand off to the auth page's no-access
          // state (menu + info dialog) instead of leaving the user on a bare "None"
          // screen. Reachable here via "Change Home" / pull-to-refresh edge cases.
          this.logger.info('fetchAndShowTenantList() - Empty tenant list; routing to /auth no-access state.');
          this.tenantListSignal.set([]);
          this.showDotsSignal.set(false);
          this.postAuthNavigationService.discardReturnUrl();
          this.navController.navigateRoot(['/auth'], { queryParams: { noAccess: '1' } });
          return of(null);
        }
        this.showTenantList(tenantList);
        return of(null);
      }),
      catchError(error => {
        this.logger.error('fetchAndShowTenantList() - Error getting tenant list: ' + error);
        this.tenantListSignal.set([]);
        this.showDotsSignal.set(false);
        if (refresher) {
          refresher.complete();
        }
        this.errorService.handleHttpClientResponseError(error, 'GET', 'fetchAndShowTenantList()')
        .subscribe(result => {
          if (result === 'unauthenticated') {
            this.logger.info('User unauthenticated');
          }
        });
        return of(null);
      })
    ).subscribe();
  }

  /**
   * Pull-to-refresh handler — always re-fetches the tenant list from the server.
   * Called from the template's ion-refresher (ionRefresh) event.
   */
  public refreshTenantList(event: RefresherCustomEvent): void {
    this.logger.info('refreshTenantList()');
    this.fetchAndShowTenantList(event);
  }

  // #################################### //
  // ### SHOW TENANT LIST ############### //
  // #################################### //

  private showTenantList(tenantList: ITenantLite[]): void {
    this.logger.info('showTenantList()');
    this.tenantListSignal.set(tenantList);
    this.currentTenantList = tenantList;
    this.showDotsSignal.set(false);
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Returns true if the tenant has a grantTenantAccess link, indicating
   * it requires a new sign-in to grant access (used in the template).
   */
  public tenantRequiresGrant(tenant: ITenantLite): boolean {
    return !!tenant.links.grantTenantAccess;
  }

  // #################################### //
  // ### PROCEED WITH SELECTED TENANT ### //
  // #################################### //

  /**
   * Called when the user clicks a tenant in the list.
   * Delegates the full activation pipeline to LoginService.
   */
  public selectTenant(tenant: ITenantLite): void {
    this.logger.info('selectTenant(): ' + JSON.stringify(tenant['tenantData']['hostingDomain']));

    if (!this.showDotsSignal()) {
      this.showDotsSignal.set(true);
    }

    this.loginService.activateTenant$(tenant).subscribe({
      next: () => {
        this.continueToApp();
      },
      error: error => {
        this.logger.error('selectTenant() - Error refreshing tenant or user');
        this.showDotsSignal.set(false);
        // Show the tenant list so the user can try another tenant instead of seeing "None"
        if (this.currentTenantList.length > 0) {
          this.logger.info('selectTenant() - Showing tenant list for recovery');
          this.tenantListSignal.set(this.currentTenantList);
        }
        this.errorService.handleHttpClientResponseError(error, 'POST/GET', 'selectTenant()')
        .subscribe(result => {
          if (result === 'unauthenticated') {
            this.logger.info('User unauthenticated');
          }
        });
      }
    });
  }

  private continueToApp(): void {
    this.logger.info('continueToApp()');
    if (this.registrationCode) {
      this.postAuthNavigationService.navigateToCardRegistration(this.registrationCode);
      return;
    }
    this.postAuthNavigationService.navigateIntoApp();
  }
}
